"use client";

import { useRef } from "react";
import { useInView } from "motion/react";
import * as m from "motion/react-m";
import { clsx } from "clsx";
import { cn } from "@ui/lib/utils";
import { Noise } from "@ui/components/core/Noise/Noise";
import { DottedBorder } from "@ui/components/core/GradientBorder";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@ui/components/core/Tooltip";
import Info16 from "@ui/icons/16x16/info-16.svg";
import { useTranslations } from "next-intl";

export interface BarChartDataPoint {
  xLabel: string;
  yValue: number;
  highlighted?: boolean | null;
}

export type BarChartTheme = "blue" | "green" | "orange";

export interface BarChartConfig {
  type: "bar";
  title?: string | null;
  description?: string | null;
  accessibilityDescription: string;
  highlightedTheme?: BarChartTheme | null;
  infoTooltip?: string | null;
  yAxisConfig: {
    labels: Array<{
      label: string;
    }>;
  };
  dataPoints: BarChartDataPoint[];
}

interface BarChartProps {
  chart: BarChartConfig;
  /**
   * Whether to show title and description caption
   */
  showCaption?: boolean;
  /**
   * Whether to include padding (default: true)
   */
  padding?: boolean;
  /**
   * Custom className for the figure element
   */
  className?: string;

  compact?: boolean;
}

const YAxisSpacer = () => {
  return <div className='w-40 md:w-48' />;
};

// Get the gradient colors based on the theme
const getHighlightedGradient = (theme: BarChartTheme | null | undefined): string => {
  switch (theme) {
    case "green":
      return "linear-gradient(to bottom, #3f4855, #578a8d, #b2a298)";
    case "orange":
      return "linear-gradient(to bottom, #b83a14, #d96432, #c48070)";
    case "blue":
    default:
      return "linear-gradient(to bottom, #1b3e72, #5f79bf, #b794ad)";
  }
};

// Calculate the percentage height for each bar
const calculateBars = (dataPoints: BarChartDataPoint[]) => {
  return dataPoints.map(point => ({
    ...point,
    heightPercent: point.yValue,
  }));
};

export const BarChart = ({ chart, showCaption = true, padding = true, className, compact = false }: BarChartProps) => {
  const { title, description, accessibilityDescription, highlightedTheme, infoTooltip, yAxisConfig, dataPoints } =
    chart;
  const chartRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(chartRef, { once: true, amount: 0.3 });
  const t = useTranslations("ClientKeys.Globals");

  const bars = calculateBars(dataPoints);
  const yAxisLabels = yAxisConfig.labels?.map(l => l.label) || [];

  return (
    <figure className={clsx("flex flex-col", padding && "p-24 md:p-48", className)} ref={chartRef}>
      {/* Chart container - hidden from screen readers as it's decorative */}
      <div className={clsx("flex", padding && "mb-40 md:mb-56")} aria-hidden='true'>
        {/* Chart area */}
        <div className='relative flex flex-1 flex-col pb-16 pt-16'>
          {/* Info tooltip in top right corner */}
          {infoTooltip && (
            <div className='absolute -top-6 right-0 z-10'>
              <TooltipProvider>
                <Tooltip delayDuration={100}>
                  <TooltipTrigger asChild>
                    <button
                      aria-label={t("learn_more")}
                      className='text-overlay-darker-plus transition-colors duration-300 ease-out hover:text-default'
                    >
                      <Info16 className='h-16 w-16' />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className='shadow-none max-w-[250px] p-8 px-12' side='left'>
                    <div className='f-paragraph-04 text-light' dangerouslySetInnerHTML={{ __html: infoTooltip }} />
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}
          {/* Grid lines and bars area */}
          <div className={clsx("relative mt-12", compact ? "h-[16.5rem]" : "h-[18.75rem]")}>
            {/* Y-axis grid lines and labels */}
            <div className='absolute inset-0 flex flex-col justify-between'>
              {yAxisLabels.map((label, index) => (
                <div key={index} className='relative h-1'>
                  <div className='f-paragraph-04 absolute top-0 w-40 shrink-0 -translate-y-full truncate text-light md:w-48'>
                    {label}
                  </div>
                  <DottedBorder side='inline' className='flex-1' />
                </div>
              ))}
              {/* Empty space for the 5th line (x-axis) */}
              <div className='h-1' />
            </div>

            {/* Bars */}
            <div className='absolute inset-0 z-2 flex items-end gap-12 md:gap-16'>
              <YAxisSpacer />
              {bars.map((bar, index) => (
                <div
                  key={index}
                  className='relative flex flex-1 items-end justify-center'
                  style={{ height: `${bar.heightPercent}%` }}
                >
                  {/* Bar */}
                  <m.div
                    className={clsx(
                      "relative h-full w-full max-w-16 overflow-hidden rounded-t-sm transition-all duration-300 ease-out md:max-w-20",
                      !bar.highlighted && "bg-darker",
                    )}
                    initial={{ transform: "scaleY(0)" }}
                    animate={
                      isInView
                        ? {
                            transform: "scaleY(1)",
                          }
                        : { transform: "scaleY(0)" }
                    }
                    transition={{
                      duration: 0.8,
                      delay: index * 0.1,
                      ease: [0.16, 1, 0.3, 1],
                    }}
                    style={{
                      transformOrigin: "bottom",
                      background: bar.highlighted ? getHighlightedGradient(highlightedTheme) : undefined,
                    }}
                  >
                    {bar.highlighted && <Noise className='h-full opacity-50' />}
                  </m.div>

                  {/* Label positioned absolutely below the bar - takes full parent width */}
                  <div className='absolute left-0 top-full w-full pt-8'>
                    <div
                      className={cn(
                        "f-paragraph-04 truncate text-center",
                        bar.highlighted ? "text-default" : "text-light",
                      )}
                    >
                      {bar.xLabel}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* X-axis line */}
          <DottedBorder side='inline' className='-mt-2' />
        </div>
      </div>

      {/* Caption contains the title, description, and accessibility description */}
      {showCaption && (
        <figcaption>
          {title && <h3 className='f-heading-04'>{title}</h3>}
          {description && <p className='f-paragraph-02 mt-8 text-light'>{description}</p>}
        </figcaption>
      )}

      {/* Accessibility description for screen readers only */}
      {accessibilityDescription && <span className='sr-only'>{accessibilityDescription}</span>}
    </figure>
  );
};
